import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { FileText, Download, Eye, Calendar, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SharedExportPage() {
  const { shareId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const { data } = await axios.get(`${API}/share/export/${shareId}/info`);
        setInfo(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    if (shareId) fetchInfo();
  }, [shareId]);

  const pdfUrl = `${API}/share/export/${shareId}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f7f4] dark:bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f7f4] dark:bg-background p-4">
        <div className="text-center space-y-3">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-medium">Shared export not found</h1>
          <p className="text-sm text-muted-foreground">This link may be invalid or the export may have been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9f7f4] dark:bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6 animate-in fade-in">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <FileText className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-2xl font-medium">Session Summary</h1>
          <p className="text-sm text-muted-foreground">Shared from Omni AI Companion</p>
        </div>

        {/* Info Card */}
        <div className="bg-white dark:bg-card border border-border/30 rounded-3xl p-5 shadow-sm space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">{info.title || "Omni Session"}</p>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              {info.module_name && (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  {info.module_name}
                </span>
              )}
              {info.message_count && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {info.message_count} messages
                </span>
              )}
              {info.created_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(info.created_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          {info.user_name && (
            <p className="text-[11px] text-muted-foreground">
              Shared by <span className="font-medium text-foreground">{info.user_name}</span>
            </p>
          )}

          <div className="space-y-2 pt-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-shared-pdf-btn"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all"
            >
              <Eye className="w-4 h-4" /> View PDF
            </a>
            <a
              href={pdfUrl}
              download
              data-testid="download-shared-pdf-btn"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full border border-border/50 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
            >
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[10px] text-muted-foreground">
          This is an AI-generated session summary. Not a clinical record.
        </p>
      </div>
    </div>
  );
}
